import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Sheet — slide-out side panel primitive used as a replacement for the
 * full-width Modal in long-form editors (ActorFormDialog, AiOperatorEditor,
 * ReadinessAssessmentDialog). Patterned after src/components/ui/Modal/Modal.tsx
 * (same hand-rolled overlay pattern; no extra dependency added). Slides in
 * from the right edge.
 *
 * Composition:
 *
 *   <Sheet open={isOpen} onClose={handleClose}>
 *     <SheetContent className="md:max-w-3xl">
 *       <SheetHeader
 *         title="Edit Actor"
 *         subtitle="Update name and assignments"
 *         badge={dirty ? <Badge variant="outline">Unsaved changes</Badge> : null}
 *         actions={<Button onClick={save}>Save</Button>}
 *       />
 *       <SheetBody>...</SheetBody>
 *       <SheetFooter>...optional...</SheetFooter>
 *     </SheetContent>
 *   </Sheet>
 *
 * `onClose` fires for every dismiss attempt: clicking the close X in the
 * header, clicking the backdrop, or pressing Escape. The consumer decides
 * whether to actually close (e.g. by showing a "discard unsaved changes?"
 * confirm before flipping `open` to false). This is how the ActorFormDialog
 * implements its dirty-state guard.
 */

interface SheetContextValue {
  onClose: () => void;
  isClosing: boolean;
}

const SheetContext = createContext<SheetContextValue>({
  onClose: () => {},
  isClosing: false,
});

export interface SheetProps {
  open: boolean;
  /**
   * Called when the user attempts to dismiss the sheet — clicking the close X,
   * clicking the backdrop, or pressing Escape. The consumer is responsible
   * for actually flipping `open` to false. This split lets the consumer
   * intercept the dismissal (for example to confirm discarding unsaved
   * changes) before the sheet animates out.
   */
  onClose: () => void;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  children?: React.ReactNode;
}

// Global reference counter for managing body scroll lock across nested sheets/modals.
let sheetOpenCount = 0;
let originalBodyOverflow: string | null = null;

export function Sheet({
  open,
  onClose,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  children,
}: SheetProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);

  // Mount / unmount around the close animation.
  useEffect(() => {
    if (open) {
      setShouldRender(true);
      setIsClosing(false);
      // Defer the "entered" flip by one frame so the panel renders off-screen
      // first and then animates in.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setHasEntered(true));
      });
    } else if (shouldRender) {
      setHasEntered(false);
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 200); // Match transition duration (--duration-fast)
      return () => clearTimeout(timer);
    }
  }, [open, shouldRender]);

  // Escape key
  useEffect(() => {
    if (!open || !closeOnEscape || isClosing) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose, closeOnEscape, isClosing]);

  // Body scroll lock (reference-counted so it composes with Modal).
  useEffect(() => {
    if (shouldRender && !isClosing) {
      if (sheetOpenCount === 0) {
        originalBodyOverflow = window.getComputedStyle(document.body).overflow;
      }
      sheetOpenCount++;
      document.body.style.overflow = 'hidden';
      return () => {
        sheetOpenCount--;
        if (sheetOpenCount === 0 && originalBodyOverflow !== null) {
          document.body.style.overflow = originalBodyOverflow;
          originalBodyOverflow = null;
        }
      };
    }
  }, [shouldRender, isClosing]);

  if (!shouldRender) return null;

  const handleBackdropClick = () => {
    if (closeOnOverlayClick && !isClosing) onClose();
  };

  return (
    <SheetContext.Provider value={{ onClose, isClosing }}>
      <div
        className="fixed inset-0 z-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* Backdrop */}
        <div
          className={cn(
            'absolute inset-0 bg-black/50 transition-opacity duration-200',
            hasEntered && !isClosing ? 'opacity-100' : 'opacity-0',
          )}
          aria-hidden="true"
          onClick={handleBackdropClick}
        />

        {/* Panel rail — flex-end so the panel hugs the right edge regardless
            of its width. */}
        <div className="absolute inset-0 flex justify-end pointer-events-none">
          <SheetPanelMount entered={hasEntered && !isClosing}>
            {children}
          </SheetPanelMount>
        </div>
      </div>
    </SheetContext.Provider>
  );
}

/**
 * Internal helper that injects the entered/isClosing state into the
 * SheetContent element via a render prop / context, so SheetContent itself
 * stays declarative and consumer-facing.
 */
const SheetMountContext = createContext<{ entered: boolean }>({ entered: false });

function SheetPanelMount({ entered, children }: { entered: boolean; children: React.ReactNode }) {
  return (
    <SheetMountContext.Provider value={{ entered }}>{children}</SheetMountContext.Provider>
  );
}

export interface SheetContentProps {
  children?: React.ReactNode;
  className?: string;
}

export function SheetContent({ children, className }: SheetContentProps) {
  const { entered } = useContext(SheetMountContext);
  return (
    <div
      className={cn(
        'pointer-events-auto h-full bg-background shadow-xl',
        'flex flex-col',
        // Default width: ~82% of the viewport on narrow screens, no upper cap.
        // Override via `className` for narrower or wider variants.
        'w-[82%] sm:max-w-none',
        'transform transition-transform duration-200 ease-out',
        entered ? 'translate-x-0' : 'translate-x-full',
        className,
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

export interface SheetHeaderProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Optional Badge displayed next to the title — typical use: status / "Unsaved changes" indicator. */
  badge?: React.ReactNode;
  /** Right-side action buttons (Save / Cancel etc). Rendered to the left of the close X. */
  actions?: React.ReactNode;
  /** Hide the built-in close X button. */
  hideClose?: boolean;
  className?: string;
}

export function SheetHeader({
  title,
  subtitle,
  badge,
  actions,
  hideClose = false,
  className,
}: SheetHeaderProps) {
  const { onClose } = useContext(SheetContext);
  return (
    <div
      className={cn(
        // sticky-at-top + border so it visually separates from the scrolling body
        'flex-shrink-0 sticky top-0 z-10 bg-background border-b',
        'flex items-center justify-between gap-4 px-6 py-4',
        className,
      )}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="min-w-0">
          {title && <h2 className="text-lg font-semibold text-foreground truncate">{title}</h2>}
          {subtitle && <p className="mt-0.5 text-sm text-muted-foreground truncate">{subtitle}</p>}
        </div>
        {badge && <div className="flex-shrink-0">{badge}</div>}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {actions}
        {!hideClose && (
          <button
            type="button"
            onClick={onClose}
            className="ml-1 rounded-md p-1.5 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Close"
          >
            <svg
              className="h-5 w-5 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

export interface SheetBodyProps {
  children?: React.ReactNode;
  className?: string;
}

export function SheetBody({ children, className }: SheetBodyProps) {
  // flex-1 + overflow-y-auto so the body scrolls internally and the sticky
  // header stays visible. min-h-0 is required so flex-1 can shrink.
  return (
    <div className={cn('flex-1 overflow-y-auto min-h-0 px-6 py-4', className)}>{children}</div>
  );
}

export interface SheetFooterProps {
  children?: React.ReactNode;
  className?: string;
}

export function SheetFooter({ children, className }: SheetFooterProps) {
  return (
    <div
      className={cn(
        'flex-shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t bg-background',
        className,
      )}
    >
      {children}
    </div>
  );
}
